import React, { Fragment } from "react"

import { useTranslation } from "react-i18next"

import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined"
import AddOutlinedIcon from "@mui/icons-material/AddOutlined"
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline"
import { Card, CardContent, Grid } from "@mui/material"
import { Button, InputText, Menu, MenuItem, Typography } from "@synapse-analytics/synapse-ui"
import { FieldArray, useFormikContext } from "formik"
import { v4 as uuidv4 } from "uuid"

import { getTheme } from "../../../hooks/UseTheme"
import type { Rule } from "../../../types/custom/rules"
import { getFieldErrorIfTouched, getRuleWeightValue } from "../../../utils/conditionHelpers"
import { getEnvVar } from "../../../utils/genericHelpers"
import { DataBlock } from "../../Ruleset/components/RuleCard"
import { EMPTY_SCORECARD_CONDITION } from "../../ScoreCardSets/Constants"
import type { GroupConditions, ScoreCardFormikValues, ScorecardRule } from "../Interfaces"
import { handleRemoveCondition as handleRemoveConditionByPath } from "../helpers"
import { Condition } from "./Condition"
import { GroupRenderer } from "./GroupRenderer"

import styles from "./RuleCard.module.scss"

type Props = {
  id?: string
  scorecardIndex?: number
  updateMode?: boolean
  /** Indicates if the form is in create mode */
  createMode: boolean
  /** Indicates if the form is in edit mode */
  editMode?: boolean
  /** Index of the rule within the scorecard */
  ruleIndex: number
  /** Indicates if the scorecard is loading */
  isScorecardLoading: boolean
}

/**
 * RuleCard component displays a rule within a scorecard.
 *
 * @param {boolean} createMode - Indicates if the form is in create mode
 * @param {boolean} [editMode] - Indicates if the form is in edit mode
 * @param {number} ruleIndex - Index of the rule within the scorecard
 * @param {boolean} isScorecardLoading - Indicates if the scorecard is loading
 * @returns {React.ReactElement} A React element representing the rule card
 */
export function RuleCard(props: Readonly<Props>): React.ReactElement {
  // destructure props
  const { ruleIndex, createMode, editMode, isScorecardLoading } = props

  // hooks
  const { values, setFieldValue, handleChange, setValues, handleBlur, isSubmitting, errors, touched } =
    useFormikContext<ScoreCardFormikValues>()
  const theme = getTheme()
  const { t } = useTranslation("scorecards")

  // states
  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement>()
  const isMenuOpen = Boolean(menuAnchor)

  // env variables
  const MAX_GROUP_DEPTH = Number(getEnvVar("SCORECARDS_GROUPS_DEPTH", 2))

  // handlers
  const addGroupHandler = (): void => {
    const newGroup: GroupConditions = {
      id: uuidv4(),
      andOr: "and",
      conditions: [EMPTY_SCORECARD_CONDITION, EMPTY_SCORECARD_CONDITION],
      groups: [],
    }
    setFieldValue(`cards[${ruleIndex}].groups`, [...(values?.cards[ruleIndex]?.groups as GroupConditions[]), newGroup])
    setMenuAnchor(undefined)
  }
  return (
    <Fragment>
      {values.cards[ruleIndex]?.type === "simple" ? (
        <Grid container style={{ height: "fit-content !important" }} item px={1.8} py={1}>
          <Condition
            isScorecardLoading={isScorecardLoading}
            path={{ groupsIdx: [], conditionIdx: 0 }}
            id={`cards[${ruleIndex}].conditions[${0}]`}
            index={0}
            isEditMode={Boolean(editMode)}
            isCreateMode={createMode}
            ruleIndex={ruleIndex}
          />
        </Grid>
      ) : (
        <Card className={createMode || editMode ? styles.card : styles.viewCard}>
          <CardContent className={styles.cardContent}>
            <Grid display="flex" justifyContent="space-between" container gap={1}>
              <Grid item xs={10}>
                {/* //  Level 1 group */}
                <FieldArray name={`cards[${ruleIndex}].conditions`}>
                  {({ push: level1push, remove: level1remove }) => (
                    <Grid container justifyContent="flex-end" alignItems="center" className={styles.groupBackground}>
                      <Grid container item spacing={1}>
                        {values?.cards[ruleIndex]?.conditions?.map((item: Rule, idx: number) => (
                          <Grid item xs={12}>
                            <Condition
                              isScorecardLoading={isScorecardLoading}
                              key={item?.id}
                              id={`cards[${ruleIndex}].conditions[${idx}]`}
                              index={idx}
                              isEditMode={Boolean(editMode)}
                              isCreateMode={createMode}
                              ruleIndex={ruleIndex}
                              path={{ conditionIdx: idx }}
                              removeCondition={() =>
                                handleRemoveConditionByPath({
                                  values,
                                  setFieldValue,
                                  setValues,
                                  levelRemove: level1remove,
                                  ruleIndex,
                                  path: {
                                    groupsIdx: [],
                                    conditionIdx: idx,
                                  },
                                })
                              }
                            />
                          </Grid>
                        ))}

                        {/* Groups (recursive) */}
                        {values?.cards[ruleIndex]?.groups && values?.cards[ruleIndex]?.groups.length > 0 && (
                          <GroupRenderer
                            groups={values?.cards[ruleIndex]?.groups}
                            ruleIndex={ruleIndex}
                            editMode={Boolean(editMode)}
                            createMode={Boolean(createMode)}
                            currentDepth={1}
                            maxDepth={MAX_GROUP_DEPTH}
                          />
                        )}

                        {/* level 1 menu button  */}
                        {(editMode || createMode) && (
                          <Grid container justifyContent="flex-start" alignItems="center">
                            <Grid item lg={12} mt={1} ml={1}>
                              <Button
                                size="regular"
                                variant="ghost"
                                onClick={(event) => setMenuAnchor(event?.currentTarget)}
                                disabled={isSubmitting}
                              >
                                + {t("addRuleBtn")}
                              </Button>
                              <Menu
                                key="basic-menu1"
                                anchorEl={menuAnchor}
                                open={isMenuOpen}
                                onClose={() => setMenuAnchor(undefined)}
                                menuMaxContent
                              >
                                <MenuItem
                                  onClick={() => {
                                    level1push({
                                      ...EMPTY_SCORECARD_CONDITION,
                                      andOr: values?.cards[ruleIndex]?.conditions?.[1]?.andOr,
                                    })
                                    setMenuAnchor(undefined)
                                  }}
                                >
                                  <Grid container display={"flex"} gap={1} height={"20px"}>
                                    <Grid item>
                                      <AddOutlinedIcon fontSize="small" htmlColor={theme.palette.base.text[1]} />
                                    </Grid>
                                    <Grid item>
                                      <Typography variant="a">{t("addRuleBtn")}</Typography>
                                    </Grid>
                                  </Grid>
                                </MenuItem>
                                <MenuItem onClick={addGroupHandler}>
                                  <Grid container display={"flex"} gap={1} height={"20px"}>
                                    <Grid item>
                                      <AddBoxOutlinedIcon fontSize="small" htmlColor={theme.palette.base.text[1]} />
                                    </Grid>
                                    <Grid item>
                                      <Typography variant="a">{t("addGroupBtn")}</Typography>
                                    </Grid>
                                  </Grid>
                                </MenuItem>
                              </Menu>
                            </Grid>
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                  )}
                </FieldArray>
              </Grid>

              <Grid
                container
                xs
                gap={0.5}
                item
                justifyContent="flex-start"
                alignItems="center"
                alignSelf="center"
                mt={1}
              >
                <Grid alignSelf="flex-end" item xs={"auto"} mr={0.5} marginTop={editMode || createMode ? 3 : 1}>
                  <Typography variant="a">{"="}</Typography>
                </Grid>

                {/* Rule Weight */}
                <Grid item xs>
                  {editMode || createMode ? (
                    <InputText
                      id={`cards[${ruleIndex}].return_value`}
                      placeholder={t("inputRuleWeightPlaceholder")}
                      value={getRuleWeightValue(values.cards[ruleIndex]?.return_value as string)}
                      handleChange={handleChange}
                      hideDescription
                      error={getFieldErrorIfTouched(errors, touched, `cards.${ruleIndex}.return_value`)}
                      handleBlur={handleBlur}
                      disabled={isSubmitting}
                      type="number"
                      fullWidth
                    />
                  ) : (
                    <DataBlock value={`${parseFloat(values.cards[ruleIndex]?.return_value as string)}`} />
                  )}
                </Grid>
              </Grid>

              {/* Remove button */}
              {(editMode || createMode) && values?.cards?.length > 1 && (
                <Grid display="flex" item alignSelf="center" mt={"2px"}>
                  <Button
                    variant="dangerous"
                    size="small"
                    disabled={isSubmitting}
                    onClick={() => {
                      const newRules = values?.cards?.filter(
                        (rule: ScorecardRule) => rule?.id !== values?.cards[ruleIndex]?.id,
                      )

                      setFieldValue(`cards`, newRules)
                    }}
                  >
                    <RemoveCircleOutlineIcon fontSize="inherit" />
                  </Button>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Fragment>
  )
}
